import type {
	CompositeFilterDescriptor,
	SortDescriptor,
} from "@tql/ui-components-react/kendo/data-query";
import type {
	GridPageChangeEvent,
	GridSortChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useRef, useState } from "react";

import { useItemsPerPagePreference } from "~/features/profile/hooks/use-items-per-page-preference.ts";

export type UseGridStateOptions = {
	initialSort?: SortDescriptor[];
	enableFilter?: boolean;
	enableClearFilters?: boolean;
};

type BaseGridState = {
	skip: number;
	take: number;
	handlePageChange: (event: GridPageChangeEvent) => void;
	// True while the persisted items-per-page preference is still loading. Feed
	// this into the grid's `loading` prop to avoid a flash of the default page
	// size before the saved preference resolves.
	isLoadingPreference: boolean;
};

type WithSort = {
	sort: SortDescriptor[];
	handleSortChange: (event: GridSortChangeEvent) => void;
};

type WithFilter = {
	filter: CompositeFilterDescriptor | undefined;
	handleFilterChange: (event: {
		filter: CompositeFilterDescriptor | null;
	}) => void;
};

type WithClearFilters = {
	gridKey: number;
	handleClearFilters: () => void;
};

export type UseGridStateReturn = BaseGridState &
	Partial<WithSort> &
	Partial<WithFilter> &
	Partial<WithClearFilters>;

/**
 * Universal grid state hook that replaces all feature-specific grid state hooks.
 * Manages pagination (skip/take), sorting (optional), filtering (optional), and
 * persistence of items-per-page preference.
 *
 * @param gridId - Numeric grid identifier from GridIdentifiers
 * @param options - Optional configuration for sort, filter, and clear filters
 * @returns Grid state object with pagination handlers and optional sort/filter state
 */
export const useGridState = (
	gridId: number,
	options?: UseGridStateOptions,
): UseGridStateReturn => {
	const {
		itemsPerPage,
		updateItemsPerPage,
		isLoading: isLoadingPreference,
	} = useItemsPerPagePreference(gridId);

	// Base pagination state
	const [skip, setSkip] = useState<number>(0);
	const [take, setTake] = useState<number>(itemsPerPage);

	// Optional sort state
	const [sort, setSort] = useState<SortDescriptor[]>(
		options?.initialSort ?? [],
	);

	// Optional filter state
	const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(
		undefined,
	);

	// Optional clear filters state
	const [gridKey, setGridKey] = useState<number>(0);

	// Once the user manually changes the page size, the local `take` becomes the
	// source of truth. Without this guard, the async preference round-trip (or a
	// stale PUT response) would re-emit the previous itemsPerPage and overwrite
	// the user's selection.
	const hasUserSetTake = useRef(false);

	// Sync take with itemsPerPage when the preference first loads (before any
	// manual interaction).
	useEffect(() => {
		if (!hasUserSetTake.current) {
			setTake(itemsPerPage);
		}
	}, [itemsPerPage]);

	// Handle page change (pagination)
	const handlePageChange = useCallback(
		(event: GridPageChangeEvent) => {
			// Prevent page changes while the preference is still loading to ensure we
			// don't overwrite the user's saved preference before it's been fetched.
			if (isLoadingPreference) return;

			setSkip(event.page.skip);
			// Convert string to number if needed (Kendo may pass string values)
			let nextTake = 25;
			if (typeof event.page.take === "number") {
				nextTake = event.page.take;
			} else if (typeof event.page.take === "string") {
				const parsed = Number(event.page.take);
				nextTake = Number.isNaN(parsed) ? 25 : parsed;
			}

			// Only persist when the page SIZE actually changed. Next/Previous page
			// navigation keeps the same `take` (only `skip` changes), so writing
			// here would schedule a redundant full-preference PUT — violating the
			// "save once per selection change" requirement.
			if (nextTake === take) return;

			hasUserSetTake.current = true;
			setTake(nextTake);
			updateItemsPerPage(nextTake);
		},
		[updateItemsPerPage, isLoadingPreference, take],
	);

	// Handle sort change (optional)
	const handleSortChange = useCallback((event: GridSortChangeEvent) => {
		setSort(event.sort);
	}, []);

	// Handle filter change (optional)
	const handleFilterChange = useCallback(
		(event: { filter: CompositeFilterDescriptor | null }) => {
			setFilter(event.filter ?? undefined);
			// Reset to first page when filter changes
			setSkip(0);
		},
		[],
	);

	// Handle clear filters (optional)
	const handleClearFilters = useCallback(() => {
		setFilter(undefined);
		setSkip(0);
		// Increment gridKey to trigger re-render of Kendo Grid
		setGridKey((prev) => prev + 1);
	}, []);

	const result: UseGridStateReturn = {
		skip,
		take,
		handlePageChange,
		isLoadingPreference,
	};

	if (options?.initialSort !== undefined) {
		result.sort = sort;
		result.handleSortChange = handleSortChange;
	}

	if (options?.enableFilter) {
		result.filter = filter;
		result.handleFilterChange = handleFilterChange;
	}

	if (options?.enableClearFilters) {
		result.gridKey = gridKey;
		result.handleClearFilters = handleClearFilters;
	}

	return result;
};
